import { useClubLogo } from '../lib/useClubLogo';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Radio } from 'lucide-react';
import { publicApi, getImageUrl } from '../lib/api';



export default function NextGameBanner() {
  const LOGO_URL = useClubLogo();
  const [featured, setFeatured] = useState(null); // { type, game }
  const [teams, setTeams] = useState([]);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([publicApi.getFeaturedGame(), publicApi.getTeams()])
      .then(([fRes, tRes]) => {
        setFeatured(fRes.data);
        setTeams(tRes.data);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  // Countdown logic for upcoming games
  useEffect(() => {
    if (!featured || featured.type !== 'next_game' || !featured.game) return;
    const updateCountdown = () => {
      const gameDate = new Date(`${featured.game.date}T${featured.game.time || '19:30'}:00`);
      const diff = gameDate - new Date();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsLive(true);
        return;
      }
      setIsLive(false);
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [featured]);

  const getOpponentInfo = (game) => {
    if (game?.opponentTeamId) {
      const team = teams.find(t => t._id === game.opponentTeamId);
      if (team) return { logo: team.logoUrl ? getImageUrl(team.logoUrl) : null, short: team.shortName || game.opponent?.substring(0, 3).toUpperCase() };
    }
    return { logo: null, short: (game?.opponent || '??').substring(0, 3).toUpperCase() };
  };

  if (loading) return (
    <div id="naechstes-spiel" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-8 animate-pulse"><div className="h-20 bg-white/5 rounded" /></div>
    </div>
  );

  if (!featured || featured.type === 'none' || !featured.game) return (
    <div id="naechstes-spiel" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="next-game-banner">
      <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-8 text-center">
        <p className="text-white/30 text-sm">Keine kommenden Spiele geplant</p>
      </div>
    </div>
  );

  const game = featured.game;
  const opp = getOpponentInfo(game);
  const isResult = featured.type === 'recent_result';
  const isWin = isResult && (game.scoreFor ?? 0) > (game.scoreAgainst ?? 0);
  const isLoss = isResult && (game.scoreFor ?? 0) < (game.scoreAgainst ?? 0);

  // Determine header label and color
  let headerLabel, headerColor, headerBg;
  if (isResult) {
    headerLabel = isWin ? 'SIEG!' : isLoss ? 'NIEDERLAGE' : 'UNENTSCHIEDEN';
    headerColor = isWin ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-yellow-400';
    headerBg = isWin ? 'bg-green-500/5' : isLoss ? 'bg-red-500/5' : 'bg-yellow-500/5';
  } else if (isLive) {
    headerLabel = 'SPIEL LÄUFT';
    headerColor = 'text-[#CC0000]';
    headerBg = 'bg-[#CC0000]/5';
  } else {
    headerLabel = 'NÄCHSTES SPIEL';
    headerColor = 'text-[#CC0000]';
    headerBg = '';
  }

  return (
    <div id="naechstes-spiel" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="next-game-banner">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className={`bg-[hsl(0,0%,7%)] border ${isResult ? (isWin ? 'border-green-500/20' : isLoss ? 'border-red-500/20' : 'border-yellow-500/20') : isLive ? 'border-[#CC0000]/30' : 'border-white/8'} rounded-2xl overflow-hidden shadow-[var(--shadow-elev-2)] relative`}>

        {/* Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="text-[8rem] sm:text-[12rem] lg:text-[16rem] font-bold text-white/[0.02] leading-none select-none" style={{ fontFamily: 'Space Grotesk' }}>
            {isResult ? 'FINAL' : isLive ? 'LIVE' : 'NEXT'}
          </span>
        </div>

        <div className="relative p-5 sm:p-8 lg:p-10">
          {/* Header */}
          <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-3 ${headerBg} -mx-5 sm:-mx-8 lg:-mx-10 -mt-5 sm:-mt-8 lg:-mt-10 px-5 sm:px-8 lg:px-10 pt-4 pb-4 mb-6 sm:mb-8`}>
            <div className="flex items-center gap-2">
              {isLive && <div className="w-2.5 h-2.5 rounded-full bg-[#CC0000] animate-pulse" />}
              {isResult && <div className={`w-2 h-2 rounded-full ${isWin ? 'bg-green-400' : isLoss ? 'bg-red-400' : 'bg-yellow-400'}`} />}
              {!isResult && !isLive && <div className="w-2 h-2 rounded-full bg-[#CC0000] animate-pulse" />}
              <span className={`text-[11px] ${headerColor} uppercase tracking-[0.2em] font-bold`}>{headerLabel}</span>
              {isLive && <Radio className="w-3.5 h-3.5 text-[#CC0000] animate-pulse" />}
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] text-white/40">
              <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /><span className="font-mono-score">{new Date(game.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span></div>
              <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /><span className="font-mono-score">{game.time || '19:30'} Uhr</span></div>
              <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /><span>{game.location === 'Heim' ? 'Heimspiel' : 'Auswärts'}</span></div>
            </div>
          </div>

          {/* Matchup */}
          <div className="flex items-center justify-center gap-4 sm:gap-8 lg:gap-16 mb-8">
            {/* Devils */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-center flex-shrink-0">
              <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden mx-auto mb-2 sm:mb-3 bg-[hsl(0,0%,10%)] p-1">
                <img src={LOGO_URL} alt="Devils" className="w-full h-full object-contain rounded-full drop-shadow-lg" style={{ background: '#141414' }} />
              </div>
              <div className="text-[9px] sm:text-[10px] text-white/30 uppercase tracking-wider mb-0.5">Dresden</div>
              <div className="text-xs sm:text-base lg:text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>DEVILS</div>
            </motion.div>

            {/* Center: VS or Score */}
            <div className="flex flex-col items-center flex-shrink-0">
              {isResult ? (
                <div className="text-center">
                  <div className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white font-mono-score tracking-wider">
                    {game.scoreFor}:{game.scoreAgainst}
                  </div>
                  <div className={`text-xs font-bold mt-2 ${isWin ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-yellow-400'}`}>
                    ENDERGEBNIS
                  </div>
                </div>
              ) : isLive ? (
                <div className="text-center">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-[#CC0000] animate-pulse" />
                    <span className="text-sm sm:text-lg font-bold text-[#CC0000] uppercase tracking-wider" style={{ fontFamily: 'Space Grotesk' }}>LIVE</span>
                  </div>
                  <div className="text-xs text-white/30">Ergebnis wird eingetragen...</div>
                </div>
              ) : (
                <div className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white/10" style={{ fontFamily: 'Space Grotesk' }}>VS</div>
              )}
            </div>

            {/* Opponent */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-center flex-shrink-0">
              <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden mx-auto mb-2 sm:mb-3 bg-[hsl(0,0%,10%)] border border-white/10 flex items-center justify-center">
                {opp.logo ? (
                  <img src={opp.logo} alt={game.opponent} className="w-full h-full object-contain p-2 rounded-full" style={{ background: '#141414' }} />
                ) : (
                  <span className="text-base sm:text-xl lg:text-3xl font-bold text-white/30" style={{ fontFamily: 'Space Grotesk' }}>{opp.short}</span>
                )}
              </div>
              <div className="text-[9px] sm:text-[10px] text-white/30 uppercase tracking-wider mb-0.5">{game.location === 'Heim' ? 'Gast' : 'Heim'}</div>
              <div className="text-xs sm:text-base lg:text-xl font-bold text-white truncate max-w-[100px] sm:max-w-none" style={{ fontFamily: 'Space Grotesk' }}>{game.opponent?.toUpperCase()}</div>
            </motion.div>
          </div>

          {/* Bottom: Countdown OR Live indicator OR Result note */}
          {!isResult && !isLive && (
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              {[{ val: countdown.days, label: 'TAGE' }, { val: countdown.hours, label: 'STD' }, { val: countdown.minutes, label: 'MIN' }, { val: countdown.seconds, label: 'SEK' }].map((item, i) => (
                <React.Fragment key={i}>
                  <div className="text-center">
                    <div className="bg-[hsl(0,0%,5%)] border border-white/8 rounded-lg px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 min-w-[40px] sm:min-w-[50px] lg:min-w-[60px]">
                      <div className="text-lg sm:text-xl lg:text-3xl font-bold text-white font-mono-score">{String(item.val).padStart(2, '0')}</div>
                    </div>
                    <div className="text-[8px] sm:text-[9px] text-white/25 mt-1 tracking-wider font-medium">{item.label}</div>
                  </div>
                  {i < 3 && <span className="text-xs sm:text-sm text-white/10 font-bold self-start mt-2 sm:mt-3">:</span>}
                </React.Fragment>
              ))}
            </div>
          )}

          {isLive && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-[#CC0000]/10 border border-[#CC0000]/20 rounded-full px-5 py-2">
                <div className="w-2 h-2 rounded-full bg-[#CC0000] animate-pulse" />
                <span className="text-xs text-[#CC0000] font-bold uppercase tracking-wider">Spiel läuft gerade</span>
                <div className="w-2 h-2 rounded-full bg-[#CC0000] animate-pulse" />
              </div>
              <p className="text-[10px] text-white/25 mt-3">Das Ergebnis wird nach Spielende eingetragen</p>
            </div>
          )}

          {isResult && (
            <div className="text-center">
              <p className="text-[10px] text-white/25">Das nächste Spiel wird in Kürze angezeigt</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
